import { useEffect, useRef, useState } from "react";

const Tinymce = ({ id, value, onEditorChange, height = 400 }) => {
    const editorRef = useRef(null);

    // get theme from localStorage
    const getTheme = () =>
        typeof window !== "undefined"
            ? localStorage.getItem("theme") || "light"
            : "light";

    const [theme, setTheme] = useState(getTheme());

    // optional: listen for external theme changes
    useEffect(() => {
        const syncTheme = () => setTheme(getTheme());

        window.addEventListener("storage", syncTheme);
        return () => window.removeEventListener("storage", syncTheme);
    }, []);

    useEffect(() => {
        if (!window.tinymce) return;

        const existing = window.tinymce.get(id);
        if (existing) existing.remove();

        const isDark = theme === "dark";

        window.tinymce.init({

            height: 200,
            min_height: 200,
            max_height: 200,

            selector: `#${id}`,
            license_key: "gpl",
            height,
            resize: false,
            menubar: false,
            branding: false,
            statusbar: false,
            plugins: "lists code",
            toolbar:
                "undo redo | bold italic underline | bullist numlist | code",

            forced_root_block: false,

            //  Light vs Dark handling
            skin: isDark ? "oxide-dark" : "oxide",

            content_style: `
                body {
                    background: ${isDark ? "#0f172a" : "#ffffff"};
                    color: ${isDark ? "#f8fafc" : "#111827"};
                    font-family: Arial, sans-serif;
                    font-size: 14px;
                    padding: 10px;
                }

                p, div, span {
                    color: ${isDark ? "#f8fafc" : "#111827"};
                }

                ::selection {
                    background: #2563eb;
                    color: white;
                }
            `,

            setup: (editor) => {
                editorRef.current = editor;

                editor.on("init", () => {
                    editor.setContent(value || "");
                });

                editor.on("change input keyup", () => {
                    onEditorChange?.(editor.getContent());
                });
            },
        });

        return () => {
            const ed = window.tinymce.get(id);
            if (ed) ed.remove();
            editorRef.current = null;
        };
    }, [id, theme]); // ✅ IMPORTANT: re-init on theme change

    // sync external value
    useEffect(() => {
        const editor = window.tinymce?.get(id);
        if (editor && editor.getContent() !== value) {
            editor.setContent(value || "");
        }
    }, [value, id]);

    return <textarea id={id} />;
};

export default Tinymce;